import { useMemo } from "react";

import { useDispatch } from "react-redux";

import { showCountryFilterHof, showDurationFilterHof } from "app/actions/WebsiteAnalysisFilterActions";
import { swSettings } from "app/common/services/swSettings";
import { allTrackers } from "app/services/track/track";
import { isNoTouchOrTrialOrAfterTrial, isSI } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

export type SelectorHandlers = {
    onDateClick?: VoidFunction;
    onCountryClick?: VoidFunction;
    onDateHover?: VoidFunction;
    onCountryHover?: VoidFunction;
    onWebSourceHover?: VoidFunction;
};

export type SelectorTriggerElementProps = {
    cursor: "pointer";
    role: "button";
    onClick: VoidFunction;
    onMouseEnter: VoidFunction;
};

export type UseSelectorTriggerAPI = {
    selectorHandlers: SelectorHandlers;
    durationTriggerProps: Partial<SelectorTriggerElementProps>;
    countryTriggerProps: Partial<SelectorTriggerElementProps>;
};

const isSelectorTriggerEnabled = (): boolean => !isSI(swSettings) && isNoTouchOrTrialOrAfterTrial(swSettings);

const createTriggerElementProps = (onClick: VoidFunction, onMouseEnter: VoidFunction): SelectorTriggerElementProps => ({
    cursor: "pointer",
    role: "button",
    onClick,
    onMouseEnter,
});

export const useSelectorTrigger = (trackingName: string): UseSelectorTriggerAPI => {
    const dispatch = useDispatch();

    return useMemo((): UseSelectorTriggerAPI => {
        if (!isSelectorTriggerEnabled()) {
            return {
                selectorHandlers: {},
                durationTriggerProps: {},
                countryTriggerProps: {},
            };
        }

        const handleDateHover = (): void => {
            allTrackers.dispatchTrackEvent("Button", "hover", `pro icons/Calendar/${trackingName}`);
        };

        const handleCountryHover = (): void => {
            allTrackers.dispatchTrackEvent("Button", "hover", `pro icons/Geography/${trackingName}`);
        };

        const handleWebSourceHover = (): void => {
            allTrackers.dispatchTrackEvent("Button", "hover", `pro icons/Devices/${trackingName}`);
        };

        const handleDateClick = (): void => {
            allTrackers.dispatchTrackEvent("Button", "click", `pro icons/Calendar/${trackingName}`);
            showDurationFilterHof(dispatch)();
        };

        const handleCountryClick = (): void => {
            allTrackers.dispatchTrackEvent("Button", "click", `pro icons/Geography/${trackingName}`);
            showCountryFilterHof(dispatch)();
        };

        const selectorHandlers: SelectorHandlers = {
            onDateClick: handleDateClick,
            onCountryClick: handleCountryClick,
            onDateHover: handleDateHover,
            onCountryHover: handleCountryHover,
            onWebSourceHover: handleWebSourceHover,
        };

        return {
            selectorHandlers,
            durationTriggerProps: createTriggerElementProps(handleDateClick, handleDateHover),
            countryTriggerProps: createTriggerElementProps(handleCountryClick, handleCountryHover),
        };
    }, [dispatch, trackingName]);
};
